import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import uuid from 'uuid';
import { smoothScrollTo } from '../../../ScrollUtils';
import { useUnmountRef } from '../../useHook';
import { useScrollingRef } from '../../useScroll';
/**
 * スライドビューワーを制御するuse
 * @param options
 * @returns {{pageUpSlide: ((function(): void)|*), changeSlideListIndex: (value: (((prevState: number) => number) | number)) => void, slideListIndex: number, setSliderRef: ((function(*): void)|*), showPageDown: boolean, imageIndexMap: {}, isEnableKeyControl, slide: *, onClickImage: ((function(*): void)|*), showPageUp: boolean, pageDownSlide: ((function(): void)|*), imageIndex: number}}
 */
const useSlideSearchOneSlideView = (options) => {
    const { changeCurrentSlide, changePage, defaultImageIndexMap, defaultSlideIndex, isEnableKeyControl, slides, } = options;
    useUnmountRef();
    // 表示しているスライドの位置を管理するstate
    const [slideListIndex, changeSlideListIndex] = useState(defaultSlideIndex || 0);
    // スライドの一覧ごとの画像がどこに表示されているかのstate
    const [imageIndexMap, changeImageIndexMap] = useState(defaultImageIndexMap || {});
    const scrollingRef = useScrollingRef();
    const containerId = useMemo(() => uuid(), []);
    const imageIndex = imageIndexMap[slideListIndex] || 0;
    const slide = slides[slideListIndex] || slides[0];
    const showPageUp = imageIndex > 0;
    const showPageDown = imageIndex < slide.pages.length - 1;
    const sliderRef = useRef({});
    // 表示中のファイルのページの更新
    useEffect(() => {
        changePage(slide.pages[imageIndex] || slide.pages[0]);
    }, [changePage, slide, imageIndex]);
    // 表示中のファイルの更新
    useEffect(() => changeCurrentSlide(slide), [changeCurrentSlide, slide]);
    /**
     * sliderのrefの設定
     * @type {(function(*): void)|*}
     */
    const setSliderRef = useCallback((el) => {
        sliderRef.current = el;
    }, [sliderRef]);
    /**
     * 対象の Element まで スムーススクロール
     * @type {(function(*): void)|*}
     */
    const slideScrollTo = useCallback((setSlideListIndex, setIndex) => {
        smoothScrollTo(`ImageSlider_${setSlideListIndex}_${setIndex}`, {
            containerId: `${containerId}_${setSlideListIndex}`,
            spy: true,
        });
    }, [containerId]);
    /**
     * スライダー左側の画像サムネをクリックした時のイベント
     * @type {(function(*): void)|*}
     */
    const onClickImage = useCallback((value) => {
        changeImageIndexMap((prev) => (Object.assign(Object.assign({}, prev), { [slideListIndex]: value })));
        slideScrollTo(slideListIndex, value);
    }, [changeImageIndexMap, slideListIndex, slideScrollTo]);
    /**
     * スライダー内のPage Upをクリックした時のイベント
     * @type {(function(): void)|*}
     */
    const pageUpSlide = useCallback(() => {
        if (!showPageUp) {
            return;
        }
        changeImageIndexMap((prev) => {
            const setPage = (prev[slideListIndex] || 0) - 1;
            slideScrollTo(slideListIndex, setPage);
            return Object.assign(Object.assign({}, prev), { [slideListIndex]: setPage });
        });
    }, [changeImageIndexMap, slideListIndex, showPageUp, slideScrollTo]);
    /**
     * スライダー内のPage Downをクリックした時のイベント
     * @type {(function(): void)|*}
     */
    const pageDownSlide = useCallback(() => {
        if (!showPageDown) {
            return;
        }
        changeImageIndexMap((prev) => {
            const setPage = (prev[slideListIndex] || 0) + 1;
            slideScrollTo(slideListIndex, setPage);
            return Object.assign(Object.assign({}, prev), { [slideListIndex]: (prev[slideListIndex] || 0) + 1 });
        });
    }, [changeImageIndexMap, slideListIndex, showPageDown, slideScrollTo]);
    useEffect(() => {
        // キーボード方向キーのスライド送り
        const onKeyDown = (e) => {
            if (!isEnableKeyControl ||
                ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
                return;
            }
            if (e.keyCode === 37) {
                sliderRef.current.slickPrev();
            }
            if (e.keyCode === 38 && showPageUp) {
                e.preventDefault();
                pageUpSlide();
            }
            if (e.keyCode === 39) {
                sliderRef.current.slickNext();
            }
            if (e.keyCode === 40 && showPageDown) {
                e.preventDefault();
                pageDownSlide();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [
        isEnableKeyControl,
        sliderRef,
        showPageUp,
        showPageDown,
        pageUpSlide,
        pageDownSlide,
    ]);
    return {
        changeImageIndexMap,
        changeSlideListIndex,
        containerId,
        imageIndex,
        imageIndexMap,
        isEnableKeyControl,
        onClickImage,
        pageDownSlide,
        pageUpSlide,
        scrollingRef,
        setSliderRef,
        showPageDown,
        showPageUp,
        slide,
        slideListIndex,
        slideScrollTo,
    };
};
export const useSlideSearchSlideView = {
    useSlideSearchOneSlideView,
};
export default useSlideSearchSlideView;
